import { useMemo } from "react";
import { ArrowUpRight, Sparkle } from "@phosphor-icons/react";
import { buildBundleCollage, collageGridClass } from "@/pages/shop/bundleCollage";
import ProductCard from "@/pages/shop/ProductCard";

/**
 * Always-visible "Featured" strip at the top of /shop and /kms/shop.
 * Renders 3 featured bundles + 1 pinned product tile so shoppers see hero
 * items even when every category is collapsed.
 *
 * Bundle clicks jump into the collapsible Bundles section (setActiveCategory
 * → "bundles") so the shopper stays on the same page and lands on the full
 * build-your-bundle flow. The featured product click opens the standard
 * ProductModal via `onOpenProduct(id)`.
 */
export default function ShopFeaturedRow({
  bundles,
  catalog = {},
  featuredProduct,
  onOpenBundles,
  onOpenProduct,
}) {
  const hasBundles = Array.isArray(bundles) && bundles.length > 0;
  const cards = useMemo(() => bundles.slice(0, 3), [bundles]);

  if (!hasBundles && !featuredProduct) return null;

  return (
    <section
      data-testid="shop-featured-row"
      className="border-b border-white/10 pb-10 md:pb-14 mb-10 md:mb-14"
    >
      <div className="flex items-center gap-3 mb-6 md:mb-8">
        <Sparkle size={14} weight="fill" className="text-[#B32027]" />
        <p className="text-[#B32027] uppercase tracking-[0.35em] text-[10px] md:text-[11px] font-bold">
          / Featured
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        {cards.map((b) => (
          <FeaturedBundleTile key={b.slug} bundle={b} catalog={catalog} onOpen={onOpenBundles} />
        ))}
        {featuredProduct && (
          <div className="flex flex-col">
            <p className="text-white/45 text-[10px] uppercase tracking-[0.3em] font-bold mb-2">
              Hero drop
            </p>
            <ProductCard
              p={featuredProduct}
              onOpen={(x) => onOpenProduct(x.id)}
            />
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Compact tile for a bundle — mirrors the collage look of the full Bundles
 * section without importing its state (modals, quotes). Clicking jumps to
 * the collapsible Bundles section where the full build flow lives.
 */
function FeaturedBundleTile({ bundle, catalog, onOpen }) {
  const collage = buildBundleCollage(bundle, catalog);
  const cover = bundle.cover_image_url;
  const priceLabel = bundle.mode === "lowest_free"
    ? "Pick 5 · Lowest FREE"
    : (bundle.price > 0 ? `$${Math.round(bundle.price)}` : "Build");

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`shop-featured-bundle-${bundle.slug}`}
      className="text-left border border-white/15 bg-[#0F0F0F] hover:border-[#B32027] transition-colors flex flex-col group"
    >
      <div className="relative aspect-square bg-[#141414] overflow-hidden">
        {cover ? (
          <img src={cover} alt={bundle.name} className="w-full h-full object-cover" />
        ) : collage.length > 0 ? (
          <div className={`grid ${collageGridClass(collage.length)} gap-0.5 w-full h-full bg-black`}>
            {collage.map((src, i) => (
              <div key={src + i} className="bg-[#141414] overflow-hidden">
                <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/30 text-xs">
            No preview
          </div>
        )}
        {bundle.badge && (
          <span className="absolute top-3 right-3 bg-[#B32027] text-white text-[9px] uppercase tracking-[0.22em] font-bold px-2.5 py-1">
            {bundle.badge}
          </span>
        )}
      </div>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <p className="text-white/45 text-[10px] uppercase tracking-[0.3em] font-bold">
          Bundle
        </p>
        <h3 className="font-display font-black uppercase tracking-tighter text-base md:text-lg leading-[0.95] flex-1">
          {bundle.name}
        </h3>
        <div className="flex items-center justify-between">
          <p className="text-white text-base md:text-lg font-bold uppercase tracking-tight">
            {priceLabel}
          </p>
          <ArrowUpRight
            size={14}
            weight="bold"
            className="text-white/50 group-hover:text-[#B32027] transition-colors"
          />
        </div>
      </div>
    </button>
  );
}
